import { useTranslation } from 'react-i18next';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { ActivityIcon, CheckCircleIcon, IssueFilledIcon, MapPinIcon, PersonIcon } from '@utils/icons';
import { tradeLabel } from '@utils/tradeLabel';
import type { IListItem } from '../../ActivitiesPage/model/listItem';
import { StarFilledIcon } from '../../IssuesPage/Board/icons';
import { DueDay } from './DueDay';
import { areasOnFloor, type ILayerChip } from './layers';
import styles from './layerChipCard.module.css';

interface ILayerChipCardProps {
  floorId: string;
  chip: ILayerChip;
  layer: 'activities' | 'issues';
  floorOfArea: ReadonlyMap<string, string>;
  /** The site's zone; dates stay hidden until it is known. */
  tz: string | undefined;
}

interface IDueProps {
  day: string | undefined;
  overdue: boolean;
  tz: string | undefined;
}

function Due({ day, overdue, tz }: IDueProps) {
  if (!day || !tz) return null;
  return (
    <span className={overdue ? `${styles.meta} ${styles.overdue}` : styles.meta}>
      <DueDay day={day} tz={tz} />
    </span>
  );
}

interface IItemBlockProps {
  item: IListItem;
  tz: string | undefined;
}

interface IActivityBlockProps extends IItemBlockProps {
  floorId: string;
  floorOfArea: ReadonlyMap<string, string>;
}

function ActivityBlock({ item, floorId, floorOfArea, tz }: IActivityBlockProps) {
  const { done, total } = areasOnFloor(item, floorId, floorOfArea);
  return (
    <li className={styles.item}>
      <span className={styles.itemTitle}>{item.title}</span>
      <span className={styles.metaRow}>
        <Due day={item.end} overdue={item.isOverdue} tz={tz} />
        <span className={styles.meta}>
          <MapPinIcon size={14} />
          {total}
        </span>
        <span className={styles.meta}>
          <CheckCircleIcon size={14} />
          {done}/{total}
        </span>
      </span>
    </li>
  );
}

function IssueBlock({ item, tz }: IItemBlockProps) {
  return (
    <li className={styles.item}>
      <span className={styles.itemTitle}>
        {item.isCritical && <StarFilledIcon size={14} />}
        {item.title}
      </span>
      <span className={styles.metaRow}>
        {item.assigneeName && (
          <span className={styles.meta}>
            <PersonIcon size={14} />
            {item.assigneeName}
          </span>
        )}
        <Due day={item.end} overdue={item.isOverdue} tz={tz} />
      </span>
    </li>
  );
}

/**
 * A building chip's hover card: the trade (pill + contractor) over one block
 * per item the chip stands for. Activities show their end date, how many of
 * their areas are on this floor and how many of those are complete; issues
 * show critical, assignee and due date. Dates flag red once overdue.
 */
export function LayerChipCard({ floorId, chip, layer, floorOfArea, tz }: ILayerChipCardProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.kindIcon}>
          {layer === 'activities' ? <ActivityIcon size={16} /> : <IssueFilledIcon size={16} />}
        </span>
        <TradePill
          label={chip.tradeId ? tradeLabel(t, chip.tradeId) : t('monitor.layers.noTrade')}
          backgroundColor={chip.color ?? undefined}
          tradeId={chip.tradeId || undefined}
          size="sm"
        />
        {chip.contractor && <span className={styles.contractor}>{chip.contractor}</span>}
      </div>
      <ul className={styles.items}>
        {chip.items.map((item) =>
          layer === 'activities' ? (
            <ActivityBlock key={item.key} item={item} floorId={floorId} floorOfArea={floorOfArea} tz={tz} />
          ) : (
            <IssueBlock key={item.key} item={item} tz={tz} />
          ),
        )}
      </ul>
    </div>
  );
}
